import { motion } from "framer-motion";
import styled, { css } from "styled-components";
import {
  COLORS,
  LAYER_COLORS,
  TEXT_COLORS,
} from "../../../constants/design/colors";
import { FONT_CSS } from "../../../constants/design/fontSize";
import {
  BORDER_RADIUS,
  SPACING,
} from "../../../constants/design/spacing";

export const LeverageTitle = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  gap: ${SPACING.two}px;
`;

export const InputButtonsWrapper = styled.div`
  display: flex;
  height: 48px;
`;

export const LeverageButton = styled(motion.button).attrs({
  type: "button",
})<{ $selected?: boolean; $hidden?: boolean }>`
  ${FONT_CSS.header.seven}
  height: 36px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border-radius: ${BORDER_RADIUS.full}px;
  border: 1px solid ${({ $selected }) =>
    $selected ? TEXT_COLORS.one : "transparent"};
  background-color: ${({ $selected }) =>
    $selected ? "transparent" : LAYER_COLORS.two};
  color: ${({ $selected }) =>
    $selected ? TEXT_COLORS.one : TEXT_COLORS.three};
  transition: all 0.2s ease-in-out;

  ${({ $hidden }) =>
    $hidden &&
    css`
      width: 0;
      opacity: 0;
      overflow: hidden;
    `}

  &:hover {
    color: ${TEXT_COLORS.one};
    border-color: ${TEXT_COLORS.one};
  }
`;

export const LeverageButtonsWrapper = styled.div`
  display: flex;
  gap: ${SPACING.two}px;
  margin-bottom: ${SPACING.three}px;
`;

/* ── Leverage Modal redesign ───────────────────────────────────── */

export const LeverageInputCard = styled.div`
  background-color: ${LAYER_COLORS.two};
  border-radius: ${BORDER_RADIUS.three}px;
  padding: 8px;
  margin-bottom: ${SPACING.three}px;
`;

export const LeverageInputCardLabel = styled.div`
  color: ${TEXT_COLORS.three};
  ${FONT_CSS.body.five}
  margin-bottom: ${SPACING.one}px;
`;

export const LeverageValueInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  color: ${TEXT_COLORS.one};
  ${FONT_CSS.header.four}
  font-weight: 600;
  padding: 0;
  caret-color: ${TEXT_COLORS.one};
`;

const TRACK_H = 20;
const THUMB_H = 24;
const TICK_ALPHA = 0.25;

const trackBackground = (fillPercent: number) => `
  linear-gradient(to right, transparent calc(25% - 1px), rgba(255,255,255,${TICK_ALPHA}) calc(25% - 1px), rgba(255,255,255,${TICK_ALPHA}) calc(25% + 1px), transparent calc(25% + 1px)),
  linear-gradient(to right, transparent calc(50% - 1px), rgba(255,255,255,${TICK_ALPHA}) calc(50% - 1px), rgba(255,255,255,${TICK_ALPHA}) calc(50% + 1px), transparent calc(50% + 1px)),
  linear-gradient(to right, transparent calc(75% - 1px), rgba(255,255,255,${TICK_ALPHA}) calc(75% - 1px), rgba(255,255,255,${TICK_ALPHA}) calc(75% + 1px), transparent calc(75% + 1px)),
  linear-gradient(to right, ${COLORS.highlight.one} 0%, ${COLORS.highlight.one} ${fillPercent}%, ${LAYER_COLORS.three} ${fillPercent}%, ${LAYER_COLORS.three} 100%)
`;

export const LeverageRangeSlider = styled.input.attrs({ type: "range" })<{
  fillPercent: number;
}>`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: ${THUMB_H}px;
  background: transparent;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-runnable-track {
    height: ${TRACK_H}px;
    border-radius: ${BORDER_RADIUS.full}px;
    background: ${({ fillPercent }) => trackBackground(fillPercent)};
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 3px;
    height: ${THUMB_H}px;
    border-radius: ${BORDER_RADIUS.full}px;
    background: ${TEXT_COLORS.one};
    cursor: pointer;
    margin-top: -${(THUMB_H - TRACK_H) / 2}px;
    margin-right:4px;
  }

  &::-moz-range-track {
    height: ${TRACK_H}px;
    border-radius: ${BORDER_RADIUS.full}px;
    background: ${({ fillPercent }) => trackBackground(fillPercent)};
  }

  &::-moz-range-thumb {
    width: 3px;
    height: ${THUMB_H}px;
    border-radius: ${BORDER_RADIUS.full}px;
    background: ${TEXT_COLORS.one};
    border: none;
    cursor: pointer;
  }
`;

export const LeverageSliderOuter = styled.div`
  margin-bottom: ${SPACING.one}px;
`;

export const LeverageMinMaxRow = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${TEXT_COLORS.three};
  ${FONT_CSS.body.five}
  margin-top: ${SPACING.one}px;
  margin-bottom: ${SPACING.three}px;
`;

export const LeverageInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.twoHalf}px;
  margin-bottom: ${SPACING.three}px;
`;

export const LeverageInfoRowItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  > span:first-child {
    color: ${TEXT_COLORS.two};
    ${FONT_CSS.body.three}
  }

  > span:last-child {
    color: ${TEXT_COLORS.one};
    ${FONT_CSS.body.three}
  }
`;

export const LeverageNoticeBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACING.two}px;
  background-color: ${LAYER_COLORS.two};
  border-radius: ${BORDER_RADIUS.three}px;
  padding: ${SPACING.three}px;
  margin-bottom: ${SPACING.three}px;

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    stroke: ${TEXT_COLORS.three};
    margin-top: 1px;
  }

  span {
    color: ${TEXT_COLORS.two};
    ${FONT_CSS.body.four}
    line-height: 1.5;
  }
`;

export const LeverageModalActions = styled.div`
  display: flex;
  gap: ${SPACING.two}px;

  > * {
    flex: 1;
  }
`;
